import { EMail } from "../EMail";
import { type EWSFolder, getEWSItem } from "./EWSFolder";
import { EWSEvent } from "../../Calendar/EWS/EWSEvent";
import { type Tag, getTagByName } from "../../Abstract/Tag";
import { Attachment, ContentDisposition } from "../../Abstract/Attachment";
import { EWSDeleteItemRequest } from "./Request/EWSDeleteItemRequest";
import { EWSUpdateItemRequest } from "./Request/EWSUpdateItemRequest";
import type { Calendar } from "../../Calendar/Calendar";
import type { EWSCalendar } from "../../Calendar/EWS/EWSCalendar";
import { PersonUID, findOrCreatePersonUID } from "../../Abstract/PersonUID";
import { InvitationMessage } from "../../Calendar/Invitation/InvitationStatus";
import { appGlobal } from "../../app";
import { sanitize } from "../../../../lib/util/sanitizeDatatypes";
import { base64ToArrayBuffer, assert, ensureArray } from "../../util/util";
import type { Collection, ArrayColl } from "svelte-collections";

const ExchangeScheduling: Record<string, number> = {
  "IPM.Schedule.Meeting.Resp.Pos": InvitationMessage.ParticipantReply,
  "IPM.Schedule.Meeting.Resp.Tent": InvitationMessage.ParticipantReply,
  "IPM.Schedule.Meeting.Resp.Neg": InvitationMessage.ParticipantReply,
  "IPM.Schedule.Meeting.Request": InvitationMessage.Invitation,
  "IPM.Schedule.Meeting.Canceled": InvitationMessage.CancelledEvent,
};

export class EWSEMail extends EMail {
  declare folder: EWSFolder;

  get itemID(): string | null {
    return this.pID as string | null;
  }
  set itemID(val: string | null) {
    assert(val == null || typeof (val) == "string", "EWS EMail itemID must be a string");
    this.pID = val;
  }

  async download() {
    let request = {
      m$GetItem: {
        m$ItemShape: {
          t$BaseShape: "IdOnly",
          t$IncludeMimeContent: true,
        },
        m$ItemIds: {
          t$ItemId: {
            Id: this.itemID,
          },
        },
      },
    };
    let result = await this.folder.account.callEWS(request);
    let mimeBase64 = sanitize.nonemptystring(getEWSItem(result.Items).MimeContent.Value);
    this.mime = new Uint8Array(await base64ToArrayBuffer(mimeBase64, "message/rfc822"));
    await this.parseMIME();
    await this.saveCompleteMessage();
  }

  fromXML(xmljs: Record<string, any>) {
    this.itemID = sanitize.nonemptystring(xmljs.ItemId.Id);
    this.id = sanitize.nonemptystring(xmljs.InternetMessageId, "");
    this.subject = sanitize.nonemptystring(xmljs.Subject, "");
    this.sent = sanitize.date(xmljs.DateTimeSent, new Date());
    this.received = sanitize.date(xmljs.DateTimeReceived, new Date());
    this.setFlags(xmljs);
    this.inReplyTo = sanitize.nonemptystring(xmljs.InReplyTo, null);
    this.references = sanitize.nonemptystring(xmljs.References, null)?.split(" ");
    if ("ReplyTo" in xmljs) {
      this.replyTo = findOrCreatePersonUID(getEmailAddressOrX400(xmljs.ReplyTo.Mailbox.EmailAddress), sanitize.nonemptystring(xmljs.ReplyTo.Mailbox.Name, null));
    }
    if ("From" in xmljs) {
      this.from = findOrCreatePersonUID(getEmailAddressOrX400(xmljs.From.Mailbox.EmailAddress), sanitize.nonemptystring(xmljs.From.Mailbox.Name, null));
    } else if ("Sender" in xmljs) {
      this.from = findOrCreatePersonUID(getEmailAddressOrX400(xmljs.Sender.Mailbox.EmailAddress), sanitize.nonemptystring(xmljs.Sender.Mailbox.Name, null));
    }
    this.outgoing = this.folder?.account.identities.some(id => id.isEMailAddress(this.from?.emailAddress));
    setPersons(this.to, xmljs.ToRecipients?.Mailbox);
    setPersons(this.cc, xmljs.CcRecipients?.Mailbox);
    setPersons(this.bcc, xmljs.BccRecipients?.Mailbox);
    this.contact = this.outgoing ? this.to.first : this.from;
    this.invitationMessage = ExchangeScheduling[xmljs.ItemClass] || InvitationMessage.None;
  }

  /** Get body and attachments from Exchange.
   * This is an alternative to parsing MIME.
   * @see EWSFolder GetItem server call. */
  bodyAndAttachmentsFromXML(xmljs: Record<string, any>) {
    assert(this.itemID == xmljs.ItemId.Id, "EWS EMail ItemID doesn't match");
    if (xmljs.Body?.BodyType == "Text") {
      this.text = sanitize.nonemptystring(xmljs.Body.Value, "");
    } else {
      this.text = sanitize.nonemptystring(xmljs.TextBody?.Value, "");
      if (xmljs.Body?.BodyType == "HTML") {
        this.html = sanitize.nonemptystring(xmljs.Body.Value, "");
      }
    }
    if (xmljs.Attachments?.FileAttachment) {
      let attachments = ensureArray(xmljs.Attachments.FileAttachment);
      this.attachments.replaceAll(attachments.map(a => {
        let attachment = new Attachment();
        attachment.filename = a.Name;
        attachment.mimeType = a.ContentType;
        attachment.disposition = a.IsInline == "true" ? ContentDisposition.inline : ContentDisposition.attachment;
        if (a.ContentId) {
          attachment.contentID = a.ContentId;
        }
        attachment.size = Number(a.Size);
        return attachment;
      }));
    }
  }

  setFlags(xmljs: Record<string, any>) {
    this.isRead = sanitize.boolean(xmljs.IsRead);
    this.isNewArrived = xmljs.ExtendedProperty?.Value == 0xFFFFFFFF; // -1?
    this.isReplied = xmljs.ExtendedProperty?.Value == 0x105;
    this.isStarred = xmljs.Flag?.FlagStatus == "Flagged";
    // can't work out how to find junk status
    this.isDraft = sanitize.boolean(xmljs.IsDraft, false);
    this.tags.replaceAll(ensureArray(xmljs.Categories?.String).map(name => getTagByName(name)));
  }

  async markRead(read = true) {
    let request = new EWSUpdateItemRequest(this.itemID, {
      MessageDisposition: "SaveOnly",
      SendMeetingInvitationsOrCancellations: "SendToNone",
      SuppressReadReceipts: true,
    });
    request.addField("Message", "IsRead", read, "message:IsRead");
    await this.folder.account.callEWS(request);
    await super.markRead(read);
  }

  async markStarred(starred = true) {
    let request = new EWSUpdateItemRequest(this.itemID, {
      MessageDisposition: "SaveOnly",
      SendMeetingInvitationsOrCancellations: "SendToNone",
      SuppressReadReceipts: true,
    });
    request.addField("Message", "Flag", {
      t$CompleteDate: null,
      t$DueDate: null,
      t$StartDate: null,
      t$FlagStatus: starred ? "Flagged" : "NotFlagged",
    }, "item:Flag");
    await this.folder.account.callEWS(request);
    await super.markStarred(starred);
  }

  async markSpam(spam = true) {
    let request = {
      m$MarkAsJunk: {
        IsJunk: spam,
        MoveItem: false,
        m$ItemIds: {
          t$ItemId: {
            Id: this.itemID,
          },
        },
      },
    };
    await this.folder.account.callEWS(request);
    await super.markSpam(spam);
  }

  async markDraft(isDraft = true) {
    await super.markDraft(isDraft);
    let request = new EWSUpdateItemRequest(this.itemID, {
      MessageDisposition: "SaveOnly",
      SendMeetingInvitationsOrCancellations: "SendToNone",
      SuppressReadReceipts: true,
    });
    request.addField("Message", "IsDraft", isDraft, "message:IsDraft");
    await this.folder.account.callEWS(request);
  }

  async deleteMessageOnServer() {
    let request = new EWSDeleteItemRequest(this.itemID, {SuppressReadReceipts: true});
    await this.folder.account.callEWS(request);
  }

  async addTagOnServer(tag: Tag) {
    await this.updateTags();
  }

  async removeTagOnServer(tag: Tag) {
    await this.updateTags();
  }

  async updateTags() {
    let request = new EWSUpdateItemRequest(this.itemID, {
      MessageDisposition: "SaveOnly",
      SendCalendarInvitationsOrCancellations: "SendToNone",
      SuppressReadReceipts: true,
    });
    request.addField("Message", "Categories", this.tags.hasItems ? { t$String: this.tags.contents.map(tag => tag.name) } : null, "item:Categories");
    await this.folder.account.callEWS(request);
  }

  getUpdateCalendars(): Collection<Calendar> {
    assert(this.invitationMessage && this.event, "Must have event to find calendar");
    if (this.invitationMessage == InvitationMessage.Invitation) {
      // EWS always puts invitations in the default calendar.
      return appGlobal.calendars.filter(calendar => calendar.mainAccount == this.folder.account && (calendar as EWSCalendar).useForInvitations);
    }
    return appGlobal.calendars.filter(calendar => calendar.mainAccount == this.folder.account && calendar.events.some(event => event.calUID == this.event.calUID));
  }

  /** EWS only provides event data for invitations,
   * but not responses to invitations.
   * Disabled, but keeping the code, in case it will be useful later.
   *
   * `EMail.loadEvent()` works for all iTIP messages.
   * By not overriding `loadEvent()` here, `EMail.loadEvent()` will be called. */
  async loadEvent_disabled() {
    assert(this.invitationMessage == InvitationMessage.Invitation, "This is not an invitation");
    assert(!this.event, "Event has already been loaded");
    let request = {
      m$GetItem: {
        m$ItemShape: {
          t$BaseShape: "Default",
          t$BodyType: "Best",
          t$AdditionalProperties: {
            t$FieldURI: [{
              FieldURI: "item:Body",
            }, {
              FieldURI: "item:ReminderIsSet",
            }, {
              FieldURI: "item:ReminderMinutesBeforeStart",
            }, {
              FieldURI: "item:LastModifiedTime",
            }, {
              FieldURI: "item:TextBody",
            }, {
              FieldURI: "calendar:IsAllDayEvent",
            }, {
              FieldURI: "calendar:MyResponseType",
            }, {
              FieldURI: "calendar:RequiredAttendees",
            }, {
              FieldURI: "calendar:OptionalAttendees",
            }, {
              FieldURI: "calendar:Recurrence",
            }, {
              FieldURI: "calendar:ModifiedOccurrences",
            }, {
              FieldURI: "calendar:DeletedOccurrences",
            }, {
              FieldURI: "calendar:UID",
            }, {
              FieldURI: "calendar:RecurrenceId",
            }],
          },
        },
        m$ItemIds: {
          t$ItemId: {
            Id: this.itemID,
          },
        },
      },
    };
    let result = await this.folder.account.callEWS(request);
    let event = new EWSEvent();
    event.fromXML(getEWSItem(result.Items));
    this.event = event;
  }
}

function setPersons(targetList: ArrayColl<PersonUID>, mailboxes: any): void {
  if (!mailboxes) {
    return;
  }
  targetList.replaceAll(ensureArray(mailboxes).map(mailbox => findOrCreatePersonUID(getEmailAddressOrX400(mailbox.EmailAddress), sanitize.nonemptystring(mailbox.Name, null))));
}

/**
 * Converts X.400 into pseudo email addresses.
 * Alternative to sanitize.emailAddress()
 * @param emailAddress email address or X.400 address
 * @returns email address
 * @throws when not valid
 */
export function getEmailAddressOrX400(emailAddress: string): string {
  if (emailAddress.toLowerCase().startsWith("/o=")) {
    return convertX400ToEmailAddress(emailAddress);
  }
  return sanitize.emailAddress(emailAddress);
}

/**
 * Converts X.400 into pseudo email addresses.
 *
 * This is just as a fail-safe. When we download the MIME and parse it,
 * we should be getting the real Internet email addresses anyways.
 *
 * @param x400 X.400 address
 * @returns pseudo email address
 */
export function convertX400ToEmailAddress(x400: string): string {
  let parts = x400.toLowerCase().split("/");
  let username = "user";
  let domain = "xfourhundred";
  for (let part of parts) {
    if (!part) {
      continue;
    }
    if (part.startsWith("o=")) {
      part = part.substring(3);
      let company = ensureAlphaNum(part);
      domain = company + "." + domain;
    }
    if (part.startsWith("ou=")) {
      part = part.substring(3);
      const prefix = "Exchange Administrative Group (";
      const suffix = ")";
      if (part.startsWith(prefix)) {
        part = part.substring(prefix.length, part.length - suffix.length);
      }
      let department = ensureAlphaNum(part);
      domain = department + "." + domain;
    }
    if (part.startsWith("cn=Recipient")) {
      continue;
    }
    if (part.startsWith("cn=")) {
      part = part.substring(3);
      username = ensureAlphaNum(part);
    }
  }
  return sanitize.emailAddress(username + "@" + domain, "must@ng");
}

function ensureAlphaNum(str: string): string {
  return str.replace(/[^a-zA-Z0-9\-]/g, "");
}
